import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import DrawerSection from '../common/Drawer/DrawerSection'
import DrawerCard from '../common/Drawer/DrawerCard'
import DrawerAddButton from '../common/Drawer/DrawerAddButton'
import DrawerPicker from '../common/Drawer/DrawerPicker'
import DrawerEmptyState from '../common/Drawer/DrawerEmptyState'
import AccoladeSelect from './AccoladeSelect'
import { adminUpdateCoach } from '../../actions/coachActions'
import { getAllAccolades } from '../../actions/accoladeActions'
import { stockAccolades } from '../../accolades/accolades'

import classes from './CoachDrawer.module.scss'

const CoachDrawerAccolades = ({ coach }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const allAccolades = useSelector(state => state.accolades.all_accolades || [])
  const [isSaving, setIsSaving] = useState(false)
  const [isAdding, setIsAdding] = useState(false)

  const userAccolades = coach.user_accolades || []
  const hasRemainingAccolades = allAccolades.length === 0
    || allAccolades.some(accolade => !userAccolades.some(userAccolade => userAccolade.accolade.id === accolade.id))

  useEffect(() => {
    if (isAdding && allAccolades.length === 0) {
      dispatch(getAllAccolades()).catch(() => toast.error(t('coaches.editAccolades.loadError'), { position: toast.POSITION.TOP_RIGHT }))
    }
  }, [isAdding, allAccolades.length, dispatch, t])

  const saveAccolades = (userAccoladesAttributes) => {
    setIsSaving(true)
    dispatch(adminUpdateCoach({ id: coach.id, user_accolades_attributes: userAccoladesAttributes }))
      .then(() => {
        toast.success(t('coaches.editAccolades.updateSuccess'), { position: toast.POSITION.TOP_RIGHT })
        setIsAdding(false)
      })
      .catch(() => toast.error(t('coaches.editAccolades.updateError'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setIsSaving(false))
  }

  return (
    <DrawerSection title={t('coaches.drawer.accolades')} hint={t('coaches.drawer.accoladesHint')}>
      {userAccolades.length === 0 && <DrawerEmptyState>{t('coaches.drawer.noAccolades')}</DrawerEmptyState>}

      {userAccolades.map(userAccolade => (
        <DrawerCard
          key={userAccolade.id}
          iconVariant='circle'
          icon={stockAccolades[userAccolade.accolade.name]?.badge
            ? <img src={stockAccolades[userAccolade.accolade.name].badge} alt='' className={`${classes.badge} w-100 h-100`} />
            : <i className='fa fa-trophy' aria-hidden='true' />}
          label={userAccolade.accolade.name}
          note={userAccolade.issued_date && t('coaches.drawer.issuedOn', { date: moment(userAccolade.issued_date).format('MMM D, YYYY') })}
          onRemove={() => saveAccolades([{ id: userAccolade.id, _destroy: true }])}
          disabled={isSaving}
        />
      ))}

      {isAdding ? (
        <DrawerPicker title={t('coaches.drawer.chooseAccolade')} onCancel={() => setIsAdding(false)}>
          <AccoladeSelect
            allAccolades={allAccolades}
            currentAccolades={userAccolades}
            onAddAccolade={(accolade) => saveAccolades([accolade])}
          />
        </DrawerPicker>
      ) : hasRemainingAccolades && (
        <DrawerAddButton onClick={() => setIsAdding(true)} disabled={isSaving}>{t('coaches.drawer.addAccolade')}</DrawerAddButton>
      )}
    </DrawerSection>
  )
}

CoachDrawerAccolades.propTypes = {
  coach: PropTypes.shape({
    id: PropTypes.number.isRequired,
    user_accolades: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      issued_date: PropTypes.string,
      accolade: PropTypes.shape({ id: PropTypes.number.isRequired, name: PropTypes.string.isRequired }).isRequired,
    })),
  }).isRequired,
}

export default CoachDrawerAccolades
